import { FeaturesDropdown } from '@/features/header/features-dropdown';
import { MobileMenu } from '@/features/header/mobile-menu';
import { FeatureCategoryId, useFeaturesCategory } from '@/providers/features-category.provider';
import { useVideoWidget } from '@/providers/video-widget.provider';
import { withBasePath } from '@/utils/basePath';
import { navLinks } from '@/utils/data/nav-links';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useState } from 'react';
import { Button } from '../ui/button';

export default function Header() {
  const [isActive, setIsActive] = useState(false);
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);
  const { isVideoExpanded } = useVideoWidget();
  const router = useRouter();
  const { activeCategory, goToCategory, goToFacturationElectronique, setActiveCategory } =
    useFeaturesCategory();

  const handleCategorySelect = (id: FeatureCategoryId) => {
    setIsFeaturesOpen(false);
    if (router.pathname !== '/') {
      setActiveCategory(id);
      void router.push('/#fonctionnalites', undefined, { scroll: false });
      return;
    }
    goToCategory(id);
  };

  useEffect(() => {
    document.body.style.overflow = isActive ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isActive]);

  return (
    <Fragment>
      <div className="border-dark-blue/[0.07] fixed z-60 w-screen backdrop-blur-lg">
        <div className="max-w-custom mx-auto flex h-24 w-full items-center justify-between px-8 py-6">
          <Link href="/" scroll={true} title="Page Home">
            <Image
              alt="Logo zLawyer"
              className="h-auto max-h-12 w-auto cursor-pointer"
              height={48}
              src={withBasePath('/images/logos/zeleLogo.png')}
              width={120}
              priority
            />
          </Link>

          {/* Desktop nav */}
          <ul className="hidden items-center gap-5 xl:flex">
            {navLinks.map((link, i) => {
              if (link.href === '/#fonctionnalites') {
                return (
                  <li
                    key={i}
                    className="relative"
                    onMouseEnter={() => setIsFeaturesOpen(true)}
                    onMouseLeave={() => setIsFeaturesOpen(false)}
                  >
                    <button
                      aria-expanded={isFeaturesOpen}
                      aria-haspopup="true"
                      className="text-md hover:text-orange flex cursor-pointer items-center gap-1 whitespace-nowrap text-gray-700 transition-colors duration-200"
                      onClick={() => handleCategorySelect(activeCategory)}
                    >
                      {link.label}
                      <svg
                        aria-hidden="true"
                        fill="none"
                        height="12"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                        width="12"
                        className={clsx(
                          'transition-transform duration-200',
                          isFeaturesOpen && 'rotate-180',
                        )}
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                    <FeaturesDropdown isOpen={isFeaturesOpen} onSelect={handleCategorySelect} />
                  </li>
                );
              }

              return (
                <li key={i}>
                  <Button
                    className="text-md hover:text-orange whitespace-nowrap text-gray-700 transition-colors duration-200"
                    href={link.href}
                    target={link.target}
                    variant="transparent"
                    isAnchor
                    onClick={(event) => {
                      if (link.href !== '/#facturation-electronique') return;

                      event.preventDefault();

                      if (router.pathname !== '/') {
                        setActiveCategory('facturation');
                        void router.push(link.href, undefined, { scroll: false });
                        return;
                      }

                      goToFacturationElectronique();
                    }}
                  >
                    {link.label}
                  </Button>
                </li>
              );
            })}
            <li className="hidden 2xl:block">
              <Button href="tel:330184257027" variant="transparent">
                01 84 25 70 27
              </Button>
            </li>
            <li>
              <Button href="/contact/" variant="fill">
                Demandez une démo
              </Button>
            </li>
          </ul>

          <div className="block h-10 w-10 xl:hidden" />
        </div>
      </div>

      <button
        aria-label="Menu"
        disabled={isVideoExpanded}
        className={clsx(
          'fixed top-7 right-8 z-300 flex h-12 w-12 items-center justify-center xl:hidden',
          'transition-opacity duration-300',
          isVideoExpanded && 'pointer-events-none opacity-0',
        )}
        onClick={() => setIsActive((v) => !v)}
      >
        <span className="flex w-6 flex-col items-end justify-center gap-[5px]">
          <span
            className={[
              'bg-orange block h-[1.5px] origin-center rounded-sm transition-all duration-300',
              isActive ? 'w-6 translate-y-[6.5px] rotate-45' : 'w-6',
            ].join(' ')}
          />
          <span
            className={[
              'bg-orange block h-[1.5px] origin-center rounded-sm transition-all duration-300',
              isActive ? 'scale-x-0 opacity-0' : 'w-4 scale-x-100 opacity-100',
            ].join(' ')}
          />
          <span
            className={[
              'bg-orange block h-[1.5px] origin-center rounded-sm transition-all duration-300',
              isActive ? 'w-6 translate-y-[-6.5px] -rotate-45' : 'w-5',
            ].join(' ')}
          />
        </span>
      </button>

      <MobileMenu isActive={isActive} onClose={() => setIsActive(false)} />
    </Fragment>
  );
}
